<template>
  <v-container fluid class="person-list-page pa-0">
    <Create
      :create-dialog="createDialog"
      @close="createDialog = false"
      @submit="getList"
    />
    <Edit
      :edit-dialog="editDialog"
      :edit-person="selectedPerson"
      @close="editDialog = false"
      @submit="getList"
    />

    <v-card :loading="loading" outlined class="person-list-card">
      <div class="person-list-header">
        <div class="title-wrap">
          <v-btn icon small class="mr-2" @click="$router.go(-1)">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <div>
            <div class="page-title">Inbound Delivery People</div>
            <div class="page-subtitle">
              {{ people.length }} delivery person{{
                people.length === 1 ? "" : "s"
              }} found
            </div>
          </div>
        </div>

        <v-btn small rounded color="primary" dark @click="createDialog = true">
          <v-icon left small>mdi-plus</v-icon>
          New Delivery Person
        </v-btn>
      </div>

      <div class="person-filter-bar">
        <v-text-field
          v-model="search"
          class="person-search"
          label="Search delivery person"
          prepend-inner-icon="search"
          outlined
          dense
          clearable
          hide-details
        />
      </div>

      <v-card-text class="pa-4">
        <div class="summary-row mb-4">
          <div class="summary-box">
            <div class="summary-label">Active</div>
            <div class="summary-value">{{ activeCount }}</div>
          </div>

          <div class="summary-box">
            <div class="summary-label">Disabled</div>
            <div class="summary-value">{{ disabledCount }}</div>
          </div>
        </div>

        <v-data-table
          :headers="headers"
          :items="people"
          :search="search"
          :loading="loading"
          :mobile-breakpoint="0"
          sort-by="name"
          class="person-table"
        >
          <template v-slot:item.name="{ item }">
            <div class="person-cell">
              <v-avatar size="38" color="blue lighten-5">
                <v-icon color="primary">mdi-account-hard-hat-outline</v-icon>
              </v-avatar>
              <div>
                <div class="font-weight-bold">{{ item.name }}</div>
                <div v-if="item.note" class="person-note">{{ item.note }}</div>
              </div>
            </div>
          </template>

          <template v-slot:item.accessCode="{ item }">
            <code class="access-code">{{ item.accessCode }}</code>
          </template>

          <template v-slot:item.active="{ item }">
            <v-chip
              small
              :color="item.active ? 'green lighten-5' : 'grey lighten-3'"
              :text-color="item.active ? 'green darken-3' : 'grey darken-2'"
            >
              {{ item.active ? "Active" : "Disabled" }}
            </v-chip>
          </template>

          <template v-slot:item.link="{ item }">
            <v-btn small text color="primary" @click="copyLink(item)">
              <v-icon small left>mdi-content-copy</v-icon>
              Copy Link
            </v-btn>
          </template>

          <template v-slot:item.action="{ item }">
            <v-menu bottom left offset-y>
              <template v-slot:activator="{ on }">
                <v-btn icon small v-on="on">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense class="menu-button">
                <v-list-item @click="openLink(item)">
                  <v-list-item-icon>
                    <v-icon small>mdi-open-in-new</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Open Link</v-list-item-title>
                </v-list-item>
                <v-list-item @click="edit(item)">
                  <v-list-item-icon>
                    <v-icon small>mdi-pencil-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Edit</v-list-item-title>
                </v-list-item>
                <v-divider />
                <v-list-item @click="remove(item)">
                  <v-list-item-icon>
                    <v-icon small color="red">mdi-delete-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="red--text">Delete</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

          <template v-slot:no-data>
            <div class="empty-state">
              <v-icon size="52" color="grey lighten-1">
                mdi-account-hard-hat-outline
              </v-icon>
              <div class="empty-title">No delivery people yet</div>
              <div class="empty-subtitle">
                Add the first person who can receive warehouse deliveries.
              </div>
              <v-btn class="mt-3" color="primary" @click="createDialog = true">
                Add Delivery Person
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import copy from "copy-to-clipboard";
import service from "../service";

const Create = () => import("./Create.vue");
const Edit = () => import("./Edit.vue");

export default {
  name: "InboundDeliveryPersonList",
  components: { Create, Edit },
  data() {
    return {
      loading: false,
      createDialog: false,
      editDialog: false,
      selectedPerson: null,
      search: "",
      people: [],
      headers: [
        { text: "Delivery Person", value: "name", align: "left" },
        { text: "Access Code", value: "accessCode", align: "left" },
        { text: "Status", value: "active", align: "left" },
        { text: "Delivery Link", value: "link", sortable: false },
        { text: "", value: "action", sortable: false, align: "right" },
      ],
    };
  },
  created() {
    document.title = "smplsale - Inbound Delivery People";
    this.getList();
  },
  computed: {
    activeCount() {
      return this.people.filter((person) => person.active).length;
    },
    disabledCount() {
      return this.people.filter((person) => !person.active).length;
    },
  },
  methods: {
    responseRows(response) {
      if (Array.isArray(response)) return response;
      if (response && Array.isArray(response.data)) return response.data;
      if (response && response.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      return [];
    },
    async getList() {
      this.loading = true;
      this.createDialog = false;
      this.editDialog = false;
      try {
        const response = await service.getAll();
        this.people = this.responseRows(response);
      } catch (error) {
        const message =
          (error.data && error.data.message) ||
          "Unable to load inbound delivery people.";
        this.$swal("Delivery People", message, "error");
      } finally {
        this.loading = false;
      }
    },
    edit(person) {
      this.selectedPerson = person;
      this.editDialog = true;
    },
    deliveryLink(person) {
      return service.getDeliveryLink(person.accessCode);
    },
    copyLink(person) {
      copy(this.deliveryLink(person));
      this.$swal
        .mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2200,
        })
        .fire({ icon: "success", title: "Delivery link copied" });
    },
    openLink(person) {
      window.open(this.deliveryLink(person), "_blank", "noopener");
    },
    async remove(person) {
      const result = await this.$swal({
        title: "Delete delivery person?",
        text: `${person.name} will no longer be able to use the warehouse link.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Delete",
      });

      if (!result.value) return;

      try {
        await service.delete(person.id);
        await this.getList();
        this.$swal("Deleted", "Delivery person has been removed.", "success");
      } catch (error) {
        const message =
          (error.data && error.data.message) ||
          "Unable to delete the delivery person.";
        this.$swal("Delivery Person", message, "error");
      }
    },
  },
};
</script>

<style scoped>
.person-list-page {
  background: #f8fafc;
}
.person-list-card {
  background: #ffffff;
  border-radius: 20px;
}
.person-list-header {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.title-wrap {
  display: flex;
  align-items: center;
}
.page-title {
  color: #0f172a;
  font-size: 22px;
  font-weight: 800;
}
.page-subtitle,
.person-note,
.empty-subtitle {
  color: #64748b;
  font-size: 13px;
  margin-top: 2px;
}
.person-filter-bar {
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.person-search {
  max-width: 340px;
  min-width: 260px;
}
.summary-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 220px));
  gap: 12px;
}
.summary-box {
  background: #f8fafc;
  border-radius: 16px;
  padding: 14px;
}
.summary-label {
  color: #64748b;
  font-size: 12px;
}
.summary-value {
  color: #0f172a;
  font-size: 22px;
  font-weight: 800;
  margin-top: 4px;
}
.person-table {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}
.person-table tr:hover {
  background: #f8fafc;
}
.person-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
}
.person-note {
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.access-code {
  background: #f1f5f9;
  border-radius: 7px;
  color: #334155;
  font-size: 11px;
  padding: 5px 7px;
}
.empty-state {
  padding: 42px 16px;
  text-align: center;
}
.empty-title {
  color: #0f172a;
  font-size: 17px;
  font-weight: 800;
  margin-top: 8px;
}
@media (max-width: 600px) {
  .person-search {
    max-width: 100%;
    min-width: 100%;
  }
  .summary-row {
    grid-template-columns: 1fr;
  }
}
</style>
